import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { useUser } from "../../../context/UserContext";
import OrderDetailsSidebar from "../../../components/common/OrderDetailsSidebar/OrderDetailsSidebar";
import useCurrency from "../../../hooks/useCurrency";
import "./OrderConfirmationPage.css";
import { useLanguage } from "../../../context/LanguageContext";
import { useDynamicPageTitle } from "../../../hooks/useDynamicPageTitle";

const OrderConfirmation = () => {
    const { orderId } = useParams();
    const { currentUser } = useUser();
    const { formatDisplay } = useCurrency();
    const { language } = useLanguage();

    // Set dynamic page title using store settings
    useDynamicPageTitle('order-confirmation');

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isLocalOrder, setIsLocalOrder] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);


    const t = {
        en: {
            pickupNotification: "Your order will be ready in 20 minutes. Come to pick it up!",
            thankYou: "THANK YOU!",
            message: "We are getting started on your order right away! A detailed order confirmation has been sent to",
            orderPlacedOn: "Your order was placed on",
            whileYouWait: "While you wait,",
            exploreText: "explore our latest arrivals and discover something new.",
            viewOrderConfirmation: "VIEW ORDER CONFIRMATION",
            orderInformation: "Order Information",
            orderId: "Your order ID",
            orderNumber: "Your order number",
            continueShopping: "Continue Shopping",
            contactSupport: "Contact Support",
            orderDetailsNotFound: "Your order has been confirmed! Detailed order information has been sent to your email. For immediate assistance, please save your order number below.",
            orderIdNotFound: "Could not retrieve order details. Please contact customer support with your order number."
        },
        ar: {
            pickupNotification: "طلبك سيكون متاحًا للاستلام خلال 20 دقيقة. ننتظرك!",
            thankYou: "شكراً لك!",
            message: "لقد بدأنا في معالجة طلبك ! تم إرسال تأكيد طلب مفصل يحتوي على جميع العناصر والأسعار ومعلومات التتبع إلى",
            orderPlacedOn: "تم استلام طلبك في",
            whileYouWait: "بينما تنتظر،",
            exploreText: "استكشف أحدث منتجاتنا واكتشف شيئاً جديداً.",
            viewOrderConfirmation: "عرض تأكيد الطلب",
            orderInformation: "معلومات الطلب",
            orderId: "رقم الطلب",
            orderNumber: "رقم الطلب",
            continueShopping: "متابعة التسوق",
            contactSupport: "تواصل مع خدمة العملاء",
            orderDetailsNotFound: "تم تأكيد طلبك! تم إرسال تفاصيل الطلب المفصلة إلى بريدك الإلكتروني. للمساعدة الفورية، يرجى حفظ رقم طلبك أدناه.",
            orderIdNotFound: "لم نتمكن من استرداد تفاصيل الطلب. يرجى التواصل مع خدمة العملاء مع إرفاق رقم طلبك."
        }
    }[language] || t.en;
    const openModal = () => {
        setShowDetailsModal(true);
    };

    const closeModal = () => {
        setShowDetailsModal(false);
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") closeModal();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, []);

    useEffect(() => {
        const fetchOrder = async () => {
            setLoading(true);
            setError("");

            // console.log("Fetching order with ID:", orderId);

            try {
                // First, check if this is a temp order ID (starts with 'temp_')
                if (orderId && orderId.startsWith('temp_')) {
                    setIsLocalOrder(true);
                    // Get the pending order from localStorage
                    const pendingOrders = JSON.parse(localStorage.getItem('pendingOrders')) || [];
                    const localOrder = pendingOrders.find(order => order.orderId === orderId);

                    if (localOrder) {
                        setOrder({
                            id: orderId,
                            ...localOrder.orderData,
                            customerInfo: localOrder.customerInfo,
                            status: "pending",
                            createdAt: localOrder.createdAt,
                            isLocalOrder: true
                        });
                    } else {
                        // No pending order found, show generic confirmation
                        setOrder({
                            id: orderId,
                            status: "pending",
                            isLocalOrder: true,
                            createdAt: new Date().toISOString()
                        });
                    }
                    setLoading(false);
                    return;
                }

                // Try to get the order directly from the store orders collection first
                // Since orderId is the document ID, try direct access first
                try {
                    const storeId = localStorage.getItem('lastStoreId') || DEFAULT_STORE_ID;
                    // console.log("Searching in storeId:", storeId);
                    // console.log("Searching for orderId:", orderId);

                    // FIRST: Try direct document access (most reliable for guests)
                    // Since orderId is usually the Firebase document ID
                    try {
                        const directOrderRef = doc(db, "stores", storeId, "orders", orderId);
                        const directOrderDoc = await getDoc(directOrderRef);

                        if (directOrderDoc.exists()) {
                            const orderData = directOrderDoc.data();
                            // console.log("✅ Order found via direct access:", orderData);
                            // console.log("Order Number:", orderData.orderNumber);
                            setOrder({
                                id: orderId,
                                documentId: directOrderDoc.id,
                                ...orderData
                            });
                            setLoading(false);
                            return;
                        } else {
                            console.log("Direct document access: Document does not exist");
                        }
                    } catch (directError) {
                        console.log("Direct document access failed:", directError);
                    }

                    // FALLBACK: Try query approaches if direct access fails
                    const ordersCollection = collection(db, "stores", storeId, "orders");
                    let orderDoc = null;
                    let orderData = null;

                    // Try query by orderId field
                    let orderQuery = query(ordersCollection, where("orderId", "==", orderId));
                    let querySnapshot = await getDocs(orderQuery);

                    if (!querySnapshot.empty) {
                        orderDoc = querySnapshot.docs[0];
                        orderData = orderDoc.data();
                        // console.log("✅ Order found via orderId query:", orderData);
                    } else {
                        // Try query by storeOrderId field
                        orderQuery = query(ordersCollection, where("storeOrderId", "==", orderId));
                        querySnapshot = await getDocs(orderQuery);

                        if (!querySnapshot.empty) {
                            orderDoc = querySnapshot.docs[0];
                            orderData = orderDoc.data();
                            // console.log("✅ Order found via storeOrderId query:", orderData);
                        }
                    }

                    if (orderData && orderDoc) {
                        // console.log("Order Number from query:", orderData.orderNumber);
                        setOrder({
                            id: orderId,
                            documentId: orderDoc.id,
                            ...orderData
                        });
                        setLoading(false);
                        return;
                    } else {
                        // console.log("❌ No order found with any query method");
                    }
                } catch (storeError) {
                    console.error("Error fetching from store orders:", storeError);
                    // Continue to try user orders
                }

                // If user is logged in and we couldn't find in store collection, try user's orders
                if (currentUser) {
                    try {
                        // Try multiple query approaches for user orders
                        const userOrdersCollection = collection(db, "users", currentUser.uid, "orders");
                        let userOrderDoc = null;
                        let userData = null;

                        // First try: Query user orders by orderId field
                        let userOrderQuery = query(userOrdersCollection, where("orderId", "==", orderId));
                        let userQuerySnapshot = await getDocs(userOrderQuery);

                        if (!userQuerySnapshot.empty) {
                            userOrderDoc = userQuerySnapshot.docs[0];
                            userData = userOrderDoc.data();
                        } else {
                            // Second try: Query user orders by storeOrderId field
                            userOrderQuery = query(userOrdersCollection, where("storeOrderId", "==", orderId));
                            userQuerySnapshot = await getDocs(userOrderQuery);

                            if (!userQuerySnapshot.empty) {
                                userOrderDoc = userQuerySnapshot.docs[0];
                                userData = userOrderDoc.data();
                            } else {
                                // Third try: Direct document access for user orders
                                try {
                                    const directUserOrderRef = doc(db, "users", currentUser.uid, "orders", orderId);
                                    const directUserOrderDoc = await getDoc(directUserOrderRef);
                                    if (directUserOrderDoc.exists()) {
                                        userOrderDoc = directUserOrderDoc;
                                        userData = directUserOrderDoc.data();
                                    }
                                } catch (directUserError) {
                                    console.log("Direct user order access failed:", directUserError);
                                }
                            }
                        }

                        if (userData && userOrderDoc) {

                            // If user order has storeOrderId reference, try to get full order details
                            if (userData.storeOrderId && userData.storeId) {
                                try {
                                    const storeOrderRef = doc(db, "stores", userData.storeId, "orders", userData.storeOrderId);
                                    const storeOrderDoc = await getDoc(storeOrderRef);

                                    if (storeOrderDoc.exists()) {
                                        const storeData = storeOrderDoc.data();
                                        setOrder({
                                            id: orderId,
                                            documentId: userData.storeOrderId, // Store the actual Firebase document ID
                                            ...storeData
                                        });
                                        // console.log("Order found via user reference:", storeData);
                                        setLoading(false);
                                        return;
                                    }
                                } catch (storeRefError) {
                                    console.error("Error fetching store order from user reference:", storeRefError);
                                }
                            }

                            // Fall back to just the user order data if we couldn't get store data
                            setOrder({
                                id: orderId,
                                documentId: userOrderDoc.id, // Store the actual Firebase document ID
                                ...userData
                            });
                            // console.log("Only user order data found:", userData);
                            setLoading(false);
                            return;
                        }
                    } catch (userError) {
                        console.error("Error fetching from user orders:", userError);
                    }
                }

                // Check localStorage for pending orders (for guests or if Firestore failed)

                // First, check for recently completed orders with full data (Stripe hosted checkout)
                const lastOrderData = JSON.parse(localStorage.getItem('lastOrderData') || '{}');
                if (lastOrderData.orderData && lastOrderData.expiresAt) {
                    // Check if the cached data hasn't expired (10 minutes)
                    const expiresAt = new Date(lastOrderData.expiresAt);
                    const now = new Date();

                    if (now < expiresAt && lastOrderData.orderData.orderId === orderId) {
                        // Use cached order data for guest user
                        setOrder({
                            id: orderId,
                            ...lastOrderData.orderData,
                            isLocalOrder: true,
                            fromCache: true
                        });
                        setLoading(false);
                        return;
                    } else if (now >= expiresAt) {
                        // Clean up expired data
                        localStorage.removeItem('lastOrderData');
                    }
                }

                // Fallback to pending orders
                const pendingOrders = JSON.parse(localStorage.getItem('pendingOrders') || '[]');
                const localOrder = pendingOrders.find(order => order.orderId === orderId);

                if (localOrder) {
                    setIsLocalOrder(true);
                    setOrder({
                        id: orderId,
                        ...localOrder.orderData,
                        customerInfo: localOrder.customerInfo,
                        status: "pending",
                        createdAt: localOrder.createdAt,
                        isLocalOrder: true
                    });
                    // console.log("Order found in localStorage:", localOrder);
                } else {
                    // Last resort: We couldn't find the order through queries, 
                    // but since orderId is shown, try one more direct access attempt
                    // console.log("❌ Order not found anywhere. Trying final direct access...");
                    // console.log("Searched for orderId:", orderId);
                    // console.log("Current storeId:", localStorage.getItem('lastStoreId') || DEFAULT_STORE_ID);

                    // Final attempt: Direct access to get just the orderNumber
                    try {
                        const storeId = localStorage.getItem('lastStoreId') || DEFAULT_STORE_ID;
                        const finalOrderRef = doc(db, "stores", storeId, "orders", orderId);
                        const finalOrderDoc = await getDoc(finalOrderRef);

                        if (finalOrderDoc.exists()) {
                            const finalOrderData = finalOrderDoc.data();
                            // console.log("✅ Order found in final attempt:", finalOrderData);
                            setOrder({
                                id: orderId,
                                documentId: finalOrderDoc.id,
                                ...finalOrderData
                            });
                            setLoading(false);
                            return;
                        }
                    } catch (finalError) {
                        console.log("Final direct access also failed:", finalError);
                    }

                    // If everything fails, show generic confirmation without error
                    setOrder({
                        id: orderId,
                        status: "confirmed",
                        createdAt: new Date().toISOString(),
                        orderNumber: null
                    });
                    setError(t.orderDetailsNotFound);
                }
            } catch (err) {
                console.error("Error fetching order:", err);
                setError(t.orderIdNotFound);
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [orderId, currentUser]);

    // Utility functions for OrderDetailsSidebar
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return "N/A";
        return parseFloat(amount || 0).toFixed(2);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;
        try {
            // Handle different timestamp formats
            if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                date = timestamp.toDate();
            } else if (timestamp instanceof Date) {
                date = timestamp;
            } else if (typeof timestamp === 'string') {
                date = new Date(timestamp);
            } else if (typeof timestamp === 'number') {
                date = new Date(timestamp);
            } else {
                return "N/A";
            }

            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        } catch (e) {
            console.error("Error formatting date:", e, timestamp);
            return "N/A";
        }
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'delivered':
                return 'status-completed';
            case 'processing':
                return 'status-processing';
            case 'shipped':
                return 'status-shipped';
            case 'cancelled':
                return 'status-cancelled';
            case 'refunded':
                return 'status-refunded';
            case 'pending':
            default:
                return 'status-pending';
        }
    };

    const formatPaymentMethod = (method) => {
        if (!method) return 'N/A';

        switch (method) {
            case 'cash_on_delivery':
                return 'Cash on Delivery';
            case 'credit_card':
                return 'Credit Card';
            case 'paypal':
                return 'PayPal';
            case 'apple_pay':
                return 'Apple Pay';
            default:
                // Format snake_case to Title Case
                return method
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
        }
    };

    const getCustomerName = (order) => {
        if (!order) return 'N/A';

        if (order.customerInfo) {
            return `${order.customerInfo.firstName || ''} ${order.customerInfo.lastName || ''}`.trim();
        } else if (order.billingInfo) {
            return `${order.billingInfo.firstName || ''} ${order.billingInfo.lastName || ''}`.trim();
        } else if (order.shippingInfo) {
            return `${order.shippingInfo.firstName || ''} ${order.shippingInfo.lastName || ''}`.trim();
        }

        return 'Guest';
    };

    if (loading) {
        return (
            <div className="order-confirmation-loading">
                <div className="loader"></div>
                <p>Loading order details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="order-confirmation-error">
                <h2>Order Information</h2>
                <p>{error}</p>
                <p>
                    Your order number: <strong>#{order?.orderNumber || 'N/A'}</strong>
                    {order?.orderNumber && orderId && (
                        <>
                            <br />
                            Order ID: <strong>{orderId}</strong>
                        </>
                    )}
                    {!order?.orderNumber && (
                        <>
                            <br />
                            Order ID: <strong>{orderId}</strong>
                        </>
                    )}
                </p>
                <div className="action-buttons">
                    <Link to="/shop" className="continue-shopping">{t.continueShopping}</Link>
                    <Link to="/contact" className="contact-support">{t.contactSupport}</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="order-confirmation">
            {/* Pickup Notification - Large Font at Top */}
            {/* <div className="pickup-notification">
                <h1 className="pickup-message">{t.pickupNotification}</h1>
            </div> */}

            <div className="thankyou-wrapper">
                <h1 className="thankyou-heading">{t.thankYou}</h1>
                <p className="thankyou-message">
                    {t.message} <strong>{order?.customerInfo?.email}</strong>.
                    {/* <br />
                    {t.orderPlacedOn} <strong>{new Date(order?.createdAt?.toDate?.() || order?.createdAt || Date.now()).toLocaleDateString()}</strong>.
                    <br /> */}
                    {order?.orderNumber && (
                        <>Your order number is <strong>#{order.orderNumber}</strong>. </>
                    )}
                    {t.whileYouWait} <Link to="/shop" className="explore-link">{t.exploreText}</Link>
                </p>
                <button className="view-order-btn" onClick={openModal}>
                    {t.viewOrderConfirmation}
                </button>
            </div>

            {/* Reuse OrderDetailsSidebar component */}
            <OrderDetailsSidebar
                selectedOrder={order}
                showDetailsSidebar={showDetailsModal}
                closeDetailsSidebar={closeModal}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getStatusClass={getStatusClass}
                formatPaymentMethod={formatPaymentMethod}
                getCustomerName={getCustomerName}
            />
        </div>
    );
};

export default OrderConfirmation;